import { useState } from "react";
import Form from "../../common/Form";
import { submitWorkerJsonForm } from "../../common/formApi";
import PhoneInputField, {
  formatPhoneForSubmit,
  isCompletePhoneNumber,
} from "../../common/PhoneInputField";
import {
  applyDateInputValidity,
  applyEmailInputValidity,
  EMAIL_INPUT_PATTERN,
  getLocalDateString,
  isSelectableFormDate,
  isValidEmail,
} from "../../common/formUtils";

function ClassRequestForm() {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    decoratingClass: "",
    classAudience: "",
    classType: "",
    preferredDate: "",
    preferredTime: "",
    details: "",
    website: "",
  });
  const [submitError, setSubmitError] = useState("");
  const [submitSuccess, setSubmitSuccess] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [phoneResetKey, setPhoneResetKey] = useState(0);

  function handleChange(
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) {
    const { name, value } = e.target;
    if (e.target instanceof HTMLInputElement) {
      if (e.target.type === "date") {
        applyDateInputValidity(e.target);
      }
      if (e.target.type === "email") {
        applyEmailInputValidity(e.target);
      }
    }
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  }

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const formEl = e.currentTarget;

    if (formData.website.trim() !== "") {
      setSubmitError("Unable to submit this form. Please try again.");
      return;
    }

    if (!isValidEmail(formData.email)) {
      setSubmitError(
        "Please enter a valid email address (e.g. name@example.com).",
      );
      return;
    }

    if (!isCompletePhoneNumber(formData.phone)) {
      setSubmitError("Please enter a valid phone number.");
      return;
    }

    if (formData.preferredDate && !isSelectableFormDate(formData.preferredDate)) {
      setSubmitError("Please choose a valid date that is today or later.");
      return;
    }

    setSubmitError("");
    setSubmitSuccess("");
    setIsSubmitting(true);

    try {
      await submitWorkerJsonForm("class", {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formatPhoneForSubmit(formData.phone),
        decoratingClass: formData.decoratingClass,
        classAudience: formData.classAudience,
        classType: formData.classType,
        preferredDate: formData.preferredDate,
        preferredTime: formData.preferredTime,
        details: formData.details,
        website: formData.website,
      });

      setSubmitSuccess(
        "Thank you! Your class inquiry was submitted successfully.",
      );
      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        decoratingClass: "",
        classAudience: "",
        classType: "",
        preferredDate: "",
        preferredTime: "",
        details: "",
        website: "",
      });
      setPhoneResetKey((key) => key + 1);
      formEl.reset();
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Could not submit right now. Please try again in a few minutes.";
      setSubmitError(message);
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <Form
      id="class-request-form"
      className="class-request-form"
      onSubmit={handleSubmit}
    >
      <div>
        <label htmlFor="class-first-name">
          First Name<span className="required">*</span>
        </label>
        <input
          type="text"
          id="class-first-name"
          name="firstName"
          onChange={handleChange}
          required
        />
      </div>
      <div>
        <label htmlFor="class-last-name">
          Last Name<span className="required">*</span>
        </label>
        <input
          type="text"
          id="class-last-name"
          name="lastName"
          onChange={handleChange}
          required
        />
      </div>
      <div>
        <label htmlFor="class-email">
          Email<span className="required">*</span>
        </label>
        <input
          type="email"
          id="class-email"
          name="email"
          autoComplete="email"
          pattern={EMAIL_INPUT_PATTERN}
          onChange={handleChange}
          onInvalid={(e) => applyEmailInputValidity(e.currentTarget)}
          required
        />
      </div>
      <div>
        <label htmlFor="class-phone">
          Phone<span className="required">*</span>
        </label>
        <PhoneInputField
          id="class-phone"
          name="phone"
          value={formData.phone}
          onChange={(phone) => setFormData((prev) => ({ ...prev, phone }))}
          reset={phoneResetKey}
        />
      </div>
      <div>
        <label htmlFor="class-type">
          Class Type<span className="required">*</span>
        </label>
        <select
          id="class-type"
          name="classType"
          onChange={handleChange}
          required
        >
          <option value="">Select one</option>
          <option value="Group">Group</option>
          <option value="Private">Private</option>
        </select>
      </div>
      <div>
        <label htmlFor="decorating-class">
          Decorating Class<span className="required">*</span>
        </label>
        <select
          id="decorating-class"
          name="decoratingClass"
          onChange={handleChange}
          required
        >
          <option value="">Select one</option>
          <option value="Cake">Cake</option>
          <option value="Cupcake">Cupcake</option>
          <option value="Cookie">Cookie</option>
          <option value="Fondant">Fondant</option>
          <option value="Flexible">Flexible</option>
        </select>
      </div>
      <div>
        <label htmlFor="class-audience">
          Audience<span className="required">*</span>
        </label>
        <select
          id="class-audience"
          name="classAudience"
          onChange={handleChange}
          required
        >
          <option value="">Select one</option>
          <option value="Adults">Adults</option>
          <option value="Kids">Kids</option>
          <option value="Corporate">Corporate</option>
        </select>
      </div>
      <div>
        <label htmlFor="preferred-date">
          Preferred Date (if looking for a specific event date)
        </label>
        <input
          type="date"
          id="preferred-date"
          name="preferredDate"
          min={getLocalDateString()}
          onChange={handleChange}
        />
      </div>
      <div>
        <label htmlFor="available-times">Available Times</label>
        <input
          type="text"
          id="available-times"
          name="preferredTime"
          placeholder="e.g. Weekday evenings and Saturdays/Sundays"
          onChange={handleChange}
        />
      </div>
      <div className="full-width">
        <label htmlFor="class-details">Additional Details</label>
        <textarea
          id="class-details"
          name="details"
          onChange={handleChange}
          placeholder="Group size, experience level, Cake4Kids volunteer, general availability, etc."
        />
      </div>
      <div className="honeypot-field" aria-hidden="true">
        <label htmlFor="class-website">Website</label>
        <input
          type="text"
          id="class-website"
          name="website"
          value={formData.website}
          onChange={handleChange}
          autoComplete="off"
          tabIndex={-1}
        />
      </div>
      <button className="btn" type="submit" disabled={isSubmitting}>
        {isSubmitting ? "Submitting..." : "Submit"}
      </button>
      {(submitError || submitSuccess) && (
        <p className="form-status">{submitError || submitSuccess}</p>
      )}
    </Form>
  );
}

export default ClassRequestForm;
